<template>
  <div id="mailbox-provider-table">
    <v-data-table
      :items="data"
      :headers="headers"
      :loading="isLoading"
      class="body-2 elevation-2"
      :options.sync="pagination"
      :footer-props="footerProps"
      :items-per-page="paginationMeta.itemsPerPage"
      :no-data-text="$dictionary.app.common.mailboxProvider.noData"
      :server-items-length="paginationMeta.serverItemsLength"
      :loading-text="$dictionary.app.common.mailboxProvider.loading"
    >
      <template #item.inbox_label="{item}">
        <status-chip :text="item.inbox_label" />
      </template>
      <template #item.name="{item}">
        <table-matrics-row
          is-text-pointer
          :text="item.name"
          text-class="blue--text"
          @click="navigateTo(item)"
        />
      </template>
      <template #item.created_at="{item}">
        <table-matrics-row :text="formatDate(item.created_at)" />
      </template>
      <template #item.updated_at="{item}">
        <table-matrics-row :text="formatDate(item.updated_at)" />
      </template>
      <template #item.seeds_count="{item}">
        <v-row no-gutters class="d-flex justify-end">
          <v-col cols="9">
            <common-icons
              classes="mr-1"
              v-if="item.has_error"
              :iconType="$appConfig.commonIcons.error"
            />
          </v-col>
          <v-col cols="3">
            <table-matrics-row
              is-link
              is-text-pointer
              :text="item.seeds_count"
              text-class="blue--text ml-2"
              :navigate-to="`${$paths.mailboxProviders}/${item.id}`"
              :query="{ active_tab: `seeds` }"
            />
          </v-col>
        </v-row>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-menu left bottom offset-y>
          <template #activator="{ on, attrs }">
            <v-btn icon v-on="on">
              <v-icon v-bind="attrs" class="text--secondary">
                mdi-dots-vertical
              </v-icon>
            </v-btn>
          </template>

          <v-list dense>
            <v-list-item @click="onDeleteMailboxProvider(item)">
              <v-icon class="mr-3 text--secondary">
                mdi-delete
              </v-icon>
              <v-list-item-title
                class="primary--font body-2 pr-3"
                v-text="'Delete Mailbox provider'"
              />
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <delete-record-dialog
      :title="deleteMailbox.title"
      :is-loading="isMailboxDeleting"
      :dialog="deleteMailbox.dialog"
      @close="deleteMailbox.dialog = false"
      @delete="deleteMailboxProvider"
      :sub-title="$dictionary.app.mailboxProvider.deleteMailbox.subTitle"
    />
  </div>
</template>

<script>
import { PaginationSortMixin, SharedUtilsMixin } from "@/mixins";
import { TableMatricsRow, StatusChip, DeleteRecordDialog } from "../shared";
import { mailboxProviderHeaders as headers } from "@/data/table-headers";

import { RESPONSE_CODES } from "@/constants";
import { deleteMailboxProvider } from "@/services";
import { isResponseCode } from "@/utils/common.utils";

import { CommonIcons } from "@/components/shared";

/**
 * Mailbox providers table
 */
export default {
  name: "MailboxProvidersTable",
  /**
   * ---------------- Mixins ------------------
   */
  mixins: [PaginationSortMixin, SharedUtilsMixin],
  /**
   * ---------------- Custom events ------------------
   */
  emits: ["update-mailbox-provider"],
  /**
   * ---------------- Props ------------------
   */
  props: {
    /**
     * Mailbox provider list (Array) to be shown in the table
     */
    data: { type: Array, default: () => [], required: true },
    /**
     * Shows loader in table
     */
    isLoading: { type: Boolean, default: false, required: true },
    /**
     * Determines if the records in the table needs to be updated
     */
    updateRecords: { type: Boolean, default: false, required: true },
    /**
     * Default sort order of the records
     */
    defaultSortOrder: { type: String, default: "" },
    searchParams: { type: Object, default: () => ({}) },
  },
  /**
   * ---------------- Components ------------------
   */
  components: {
    StatusChip,
    CommonIcons,
    TableMatricsRow,
    DeleteRecordDialog,
  },
  /**
   * ---------------- Data properties ------------------
   */
  data() {
    const {
      title,
      subTitle,
    } = this.$dictionary.app.mailboxProvider.deleteMailbox;
    return {
      headers,
      isMailboxDeleting: false,
      deleteMailbox: {
        dialog: false,
        title,
        subTitle,
      },
      selectedMailbox: null,
    };
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    /**
     * Navigates user to maibox provider show page
     * @listens click event
     */
    navigateTo({ id }) {
      this.$router.push({
        name: "mailbox-provider",
        params: { id },
        query: { active_tab: "profile" },
      });
    },
    /**
     * Formats date into am and pm date format
     * @returns {String} Fomatted date
     */
    formatDate(date) {
      const { formatDateToAmPm } = this.$options.filters;
      return formatDateToAmPm(date);
    },
    /**
     * Updates mailbox provider
     * @emits update-mailbox-provider with mailbox provider record to be updated
     */
    updateMailboxProvider(item) {
      this.$emit("update-mailbox-provider", { ...item });
    },
    /**
     * Handles delete mailbox provider event
     * @description Shows delete mailbox provider dialog box to delete the mailbox provider
     */
    onDeleteMailboxProvider(item) {
      const { title } = this.$dictionary.app.mailboxProvider.deleteMailbox;
      this.selectedMailbox = item;
      this.deleteMailbox.dialog = true;
      this.deleteMailbox.title = title.replace("<<mailbox>>", item.name);
    },
    /**
     * Deletes selected mailbox provider
     * @description Sends a delte api request to delete the selected mailbox provider
     */
    async deleteMailboxProvider() {
      try {
        if (!this.selectedMailbox) return;
        const { id } = this.selectedMailbox;

        this.isMailboxDeleting = true;
        const { status } = await deleteMailboxProvider(id);

        if (isResponseCode(status, RESPONSE_CODES.success)) {
          const { deleteMailbox } = this.$dictionary.app.mailboxProvider;
          this.setSnackbar({
            value: true,
            message: deleteMailbox.successMsg,
            type: this.snackbarTypes.success,
          });
        }
        this.selectedMailbox = null;
        this.deleteMailbox.dialog = false;
        this.getRecords(this.searchParams);
      } finally {
        this.isMailboxDeleting = false;
      }
    },
  },
  /**
  |--------------------------------------------------
  | Watching properties
  |--------------------------------------------------
  */
  watch: {
    /**
     * Fetches latest mailbox provider record
     */
    updateRecords() {
      this.getRecords(this.searchParams);
    },
  },
  /**
  |--------------------------------------------------
  | Created lifecycle hook
  |--------------------------------------------------
  */
  created() {
    this.getRecords();
  },
};
</script>
